import { requestBidsAndSetTargeting } from 'client/utils/header-bidding/run-prebid-auction';

const registeredSlots = new Map();
const slotRegisteredSubscribers = new Set();

let pageTargeting = {};
let appliedTargetingKeys = new Set();
let initialLoadStarted = false;
let headerBiddingAllowed = false;
let servicesEnabled = false;
let eligibilityVersion = 0;
const inFlightRequests = new Map();
const pendingBatches = new Map();
const slotBatches = new Map();
const SLOT_READINESS_TIMEOUT_MS = 1000;

// Unlike legacy setTargeting(), GPT setConfig() rejects targeting containing numbers or booleans.
function normalizeTargetingValue(value) {
  return Array.isArray(value) ? value.filter(item => item != null).map(String) : String(value);
}

function getGoogletag() {
  if (typeof window === 'undefined') {
    return null;
  }

  window.googletag = window.googletag || {};
  window.googletag.cmd = window.googletag.cmd || [];
  return window.googletag;
}

function runGptCommand(callback) {
  const googletag = getGoogletag();
  if (!googletag) {
    return Promise.resolve(null);
  }

  return new Promise(resolve => {
    googletag.cmd.push(() => {
      try {
        resolve(callback(googletag));
      } catch {
        resolve(null);
      }
    });
  });
}

function applyPageTargeting(googletag) {
  const targeting = {};

  appliedTargetingKeys.forEach(key => {
    if (!Object.prototype.hasOwnProperty.call(pageTargeting, key) || pageTargeting[key] == null) {
      targeting[key] = null;
    }
  });

  const nextAppliedKeys = new Set();
  Object.entries(pageTargeting).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      targeting[key] = normalizeTargetingValue(value);
      nextAppliedKeys.add(key);
    }
  });

  if (Object.keys(targeting).length) {
    googletag.setConfig({ targeting });
  }
  appliedTargetingKeys = nextAppliedKeys;
}

async function defineSlots(slots) {
  return runGptCommand(googletag => {
    const pubads = googletag.pubads();
    applyPageTargeting(googletag);

    const definedSlots = slots.reduce((result, slotConfig) => {
      if (registeredSlots.get(slotConfig.slotId) !== slotConfig) {
        return result;
      }
      if (slotConfig.gptSlot) {
        result.push(slotConfig);
        return result;
      }
      const gptSlot = googletag
        .defineSlot(slotConfig.adUnitPath, slotConfig.sizes, slotConfig.slotId)
        ?.addService(pubads);

      if (!gptSlot) {
        return result;
      }

      const slotTargeting = Object.fromEntries(
        Object.entries(slotConfig.targetingArguments || {})
          .filter(([, value]) => value != null)
          .map(([key, value]) => [key, normalizeTargetingValue(value)])
      );
      if (Object.keys(slotTargeting).length) {
        gptSlot.setConfig({ targeting: slotTargeting });
      }

      slotConfig.gptSlot = gptSlot; // eslint-disable-line no-param-reassign
      googletag.display(slotConfig.slotId);
      result.push(slotConfig);
      return result;
    }, []);

    if (!servicesEnabled) {
      googletag.enableServices();
      servicesEnabled = true;
    }

    return definedSlots;
  });
}

function requestSlots(slots, registerAdUnits = true) {
  const busy = slots.map(slot => inFlightRequests.get(slot)).filter(Boolean);
  const available = slots.filter(slot => !inFlightRequests.has(slot));
  if (busy.length && available.length && slots.some(slot => slot.requestGroupSize > 1)) {
    return Promise.all(busy).then(() => requestSlots(slots, registerAdUnits));
  }
  if (!available.length) {
    return Promise.all(busy).then(() => undefined);
  }
  const version = eligibilityVersion;
  const isCurrent = () =>
    version === eligibilityVersion && available.every(slot => registeredSlots.get(slot.slotId) === slot);
  const execute = async () => {
    const definedSlots = await defineSlots(available);
    if (!definedSlots?.length || !isCurrent()) {
      return;
    }
    if (headerBiddingAllowed) {
      const adUnits = definedSlots.map(slot => slot.prebidAdUnit).filter(Boolean);
      const adUnitCodes = adUnits.map(adUnit => adUnit.code);
      if (adUnitCodes.length) {
        await requestBidsAndSetTargeting({
          ...(registerAdUnits ? { adUnits } : { registerAdUnits: false }),
          adUnitCodes,
          isCurrent: () => headerBiddingAllowed && isCurrent(),
        });
      }
    }
    await runGptCommand(googletag => {
      if (isCurrent()) {
        applyPageTargeting(googletag);
        googletag.pubads().refresh(definedSlots.map(slot => slot.gptSlot));
      }
    });
  };
  const request = execute().finally(() => {
    available.forEach(slot => {
      if (inFlightRequests.get(slot) === request) {
        inFlightRequests.delete(slot);
      }
    });
  });
  available.forEach(slot => inFlightRequests.set(slot, request));
  return Promise.all([...busy, request]).then(() => undefined);
}

export function setPageTargeting(nextTargeting) {
  pageTargeting = nextTargeting || {};
  if (servicesEnabled) {
    runGptCommand(googletag => applyPageTargeting(googletag));
  }
}

function flushBatch(batch) {
  clearTimeout(batch.timer);
  clearTimeout(batch.deadline);
  pendingBatches.delete(batch.key);
  batch.reservations.forEach(slotId => slotBatches.delete(slotId));
  batch.slots.forEach(slot => slotBatches.delete(slot.slotId));
  requestSlots(Array.from(batch.slots.values())).then(batch.resolve, batch.reject);
}

function scheduleBatch(batch) {
  const pendingBatch = batch;
  if (!initialLoadStarted) {
    return;
  }
  if (batch.deadline === null && !batch.expired) {
    pendingBatch.deadline = setTimeout(() => {
      pendingBatch.expired = true;
      if (batch.atomic && (batch.reservations.size || batch.slots.size < batch.expectedCount)) {
        // Keep incomplete filmstrips intact without holding the initial-load promise.
        batch.resolve();
      } else {
        flushBatch(batch);
      }
    }, SLOT_READINESS_TIMEOUT_MS);
  }
  if (batch.timer !== null || batch.reservations.size || batch.slots.size < batch.expectedCount) {
    return;
  }
  pendingBatch.timer = setTimeout(() => {
    pendingBatch.timer = null;
    if (!batch.reservations.size && batch.slots.size >= batch.expectedCount) {
      flushBatch(batch);
    }
  }, 0);
}

function getBatch(key = 'page', atomic = false, expectedCount = 0) {
  if (!pendingBatches.has(key)) {
    const batch = {
      key,
      atomic,
      expectedCount,
      expired: false,
      reservations: new Set(),
      slots: new Map(),
      timer: null,
      deadline: null,
    };
    batch.promise = new Promise((resolve, reject) => {
      batch.resolve = resolve;
      batch.reject = reject;
    });
    if (atomic) {
      registeredSlots.forEach(slot => {
        if (slot.requestGroup === key) {
          batch.slots.set(slot.slotId, slot);
          slotBatches.set(slot.slotId, batch);
        }
      });
    }
    pendingBatches.set(key, batch);
  }
  return pendingBatches.get(key);
}

export function reserveGptSlotV2(slotId, group = 'page', { atomic = false, expectedCount = 0 } = {}) {
  if (registeredSlots.has(slotId) || slotBatches.has(slotId)) {
    return;
  }
  const batch = getBatch(group, atomic, expectedCount);
  batch.reservations.add(slotId);
  slotBatches.set(slotId, batch);
  scheduleBatch(batch);
  slotRegisteredSubscribers.forEach(callback => callback(slotId));
}

export function registerGptSlotV2(slotConfig) {
  if (registeredSlots.has(slotConfig.slotId)) {
    return registeredSlots.get(slotConfig.slotId);
  }
  const batch = slotBatches.get(slotConfig.slotId) || getBatch(slotConfig.requestGroup);
  batch.reservations.delete(slotConfig.slotId);
  const slot = { ...slotConfig, gptSlot: null };
  registeredSlots.set(slotConfig.slotId, slot);
  slotBatches.set(slotConfig.slotId, batch);
  batch.slots.set(slotConfig.slotId, slot);
  slotRegisteredSubscribers.forEach(callback => callback(slotConfig.slotId));
  scheduleBatch(batch);
  return registeredSlots.get(slotConfig.slotId);
}

export function unregisterGptSlotV2(slotId) {
  const slot = registeredSlots.get(slotId);
  registeredSlots.delete(slotId);
  const batch = slotBatches.get(slotId);
  slotBatches.delete(slotId);
  if (batch) {
    batch.slots.delete(slotId);
    batch.reservations.delete(slotId);
    if (!batch.slots.size && !batch.reservations.size) {
      clearTimeout(batch.timer);
      clearTimeout(batch.deadline);
      pendingBatches.delete(batch.key);
      batch.resolve();
    } else {
      scheduleBatch(batch);
    }
  }
  if (slot?.prebidAdUnit && typeof window !== 'undefined' && window.pbjs) {
    const pbjs = window.pbjs;
    pbjs.que = pbjs.que || [];
    pbjs.que.push(() => pbjs.removeAdUnit?.(slot.prebidAdUnit.code));
  }
  if (slot?.gptSlot) {
    runGptCommand(googletag => googletag.destroySlots([slot.gptSlot]));
  }
}

export function subscribeSlotRegistered(callback) {
  slotRegisteredSubscribers.add(callback);
  return () => slotRegisteredSubscribers.delete(callback);
}

export function getRegisteredSlotCount() {
  return new Set([...registeredSlots.keys(), ...slotBatches.keys()]).size;
}

export function setHeaderBiddingAllowed(allowed = false) {
  if (headerBiddingAllowed !== Boolean(allowed)) {
    eligibilityVersion += 1;
    inFlightRequests.clear();
    if (!allowed) {
      runGptCommand(() => {
        registeredSlots.forEach(slot => {
          const keys = slot.gptSlot?.getTargetingKeys?.() || [];
          keys.filter(key => key.startsWith('hb_')).forEach(key => slot.gptSlot.clearTargeting(key));
        });
      });
    }
  }
  headerBiddingAllowed = Boolean(allowed);
}

export function startInitialGptLoad({ isHeaderBiddingAllowed = false } = {}) {
  if (initialLoadStarted) {
    return Promise.resolve();
  }

  initialLoadStarted = true;
  headerBiddingAllowed = isHeaderBiddingAllowed;
  const batches = Array.from(pendingBatches.values());
  batches.forEach(scheduleBatch);
  return Promise.all(batches.map(batch => batch.promise)).then(() => undefined);
}

export function refreshGptSlotsV2(...slotIds) {
  const slots = slotIds.length
    ? slotIds.map(slotId => registeredSlots.get(slotId)).filter(Boolean)
    : Array.from(registeredSlots.values()).filter(slot => !slot.shouldRefresh || slot.shouldRefresh());

  const groups = new Set(slots.map(slot => slot.requestGroup).filter(group => group && group !== 'page'));
  const groupedSlots = new Map(slots.map(slot => [slot.slotId, slot]));
  const readyGroupCounts = new Map();
  registeredSlots.forEach(slot => {
    if (slot.gptSlot) {
      readyGroupCounts.set(slot.requestGroup, (readyGroupCounts.get(slot.requestGroup) || 0) + 1);
    }
    if (groups.has(slot.requestGroup)) {
      groupedSlots.set(slot.slotId, slot);
    }
  });
  const definedSlots = Array.from(groupedSlots.values()).filter(
    slot =>
      slot.gptSlot &&
      !pendingBatches.get(slot.requestGroup)?.atomic &&
      (readyGroupCounts.get(slot.requestGroup) || 0) >= (slot.requestGroupSize || 0)
  );
  if (!definedSlots.length) {
    return Promise.resolve();
  }

  return requestSlots(definedSlots, false);
}

export function resetGptRuntimeV2ForTest() {
  pendingBatches.forEach(batch => {
    clearTimeout(batch.timer);
    clearTimeout(batch.deadline);
    batch.resolve();
  });
  eligibilityVersion += 1;
  inFlightRequests.clear();
  pendingBatches.clear();
  slotBatches.clear();
  registeredSlots.clear();
  slotRegisteredSubscribers.clear();
  pageTargeting = {};
  appliedTargetingKeys = new Set();
  initialLoadStarted = false;
  headerBiddingAllowed = false;
  servicesEnabled = false;
}
